import React, { Component } from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, CartIcon, AlertIcon, LeafIcon, BoneIcon, SparkleIcon, BookIcon } from './Icons';
interface BonusData {
  number: number;
  title: string;
  description: string;
  value: string;
  icon: ComponentType<{
    className?: string;
  }>;
  iconColor: string;
  mockupColor: string;
  image?: string;
  imageAlt?: string;
}
const bonuses: BonusData[] = [{
  number: 1,
  title: 'The 15-Minute Meal Prep Guide',
  description: 'One 15-minute Sunday session. Seven days of longevity bowls ready to go. No cooking skills needed — just chop, portion, and store.',
  value: '$27.00',
  icon: ClockIcon,
  iconColor: 'text-gold',
  mockupColor: 'from-gold/20 to-gold/5',
  image: 'https://cdn.magicpatterns.com/uploads/a1K3TTuJahjZ7JQ1h5wLcR/imagen_1.png',
  imageAlt: 'The 15-Minute Meal Prep Guide shown on a tablet with fresh sardines, egg, and blueberries'
}, {
  number: 2,
  title: 'The Supermarket Cheat Sheet',
  description: 'The exact aisles, brands, and products to grab at Costco, Walmart, or Aldi. Walk in with a list, walk out with everything your dog needs for pennies per serving.',
  value: '$27.00',
  icon: CartIcon,
  iconColor: 'text-green-accent',
  mockupColor: 'from-green-accent/20 to-green-accent/5',
  image: 'https://cdn.magicpatterns.com/uploads/cJdPV5aCiypUeZBJCJ7U53/supermarket.png',
  imageAlt: 'Supermarket Cheat Sheet shown on a phone with a checklist of grocery items'
}, {
  number: 3,
  title: 'The "Poison List" (Hidden Toxins)',
  description: 'The ingredients hiding in plain sight on your kibble bag that are silently fueling inflammation, allergies, and joint breakdown. Know exactly what to avoid.',
  value: '$47.00',
  icon: AlertIcon,
  iconColor: 'text-urgency-red-bright',
  mockupColor: 'from-urgency-red/20 to-urgency-red/5',
  image: 'https://cdn.magicpatterns.com/uploads/oSLEyk1etA5HuQJdcehL8C/pison_list.png',
  imageAlt: 'The Poison List book and tablet mockup showing hidden toxins in dog food'
}, {
  number: 4,
  title: 'The "Itchy Dog" Protocol',
  description: 'A targeted plan to calm chronic scratching, hot spots, and skin irritation using specific anti-inflammatory toppers. Most owners see results within 2 weeks.',
  value: '$67.00',
  icon: LeafIcon,
  iconColor: 'text-green-accent',
  mockupColor: 'from-green-accent/20 to-green-accent/5',
  image: 'https://cdn.magicpatterns.com/uploads/tHK8bCnJ4ZRsY5C2j5ifRm/itchy.png',
  imageAlt: 'The Itchy Dog Protocol shown on a tablet with Omega-3 oil bottle and flowers'
}, {
  number: 5,
  title: 'Giant Breed Joint Rescue',
  description: 'The specific nutrients, portions, and timing to support joints under 100+ lbs of pressure. Designed for Cane Corsos, Mastiffs, Great Danes, and other giants.',
  value: '$47.00',
  icon: BoneIcon,
  iconColor: 'text-gold',
  mockupColor: 'from-gold/20 to-gold/5',
  image: 'https://cdn.magicpatterns.com/uploads/wDwH6PooJi4qMbmrAXrAFd/joint_ok.png',
  imageAlt: 'Giant Breed Joint Protocol shown on a tablet with dog skeleton highlighting joint areas'
}];
function MockupGraphic({
  icon: Icon,
  iconColor,
  gradientClass
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  iconColor: string;
  gradientClass: string;
}) {
  return <div className="relative w-full h-full flex items-center justify-center" data-id="element-1">
      {/* Tablet frame */}
      <div className="relative w-32 sm:w-40 h-44 sm:h-52" data-id="element-2">
        {/* Glow */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradientClass} blur-2xl rounded-full opacity-60`} data-id="element-3" />
        {/* Device */}
        <div className="relative w-full h-full bg-bg-primary border-2 border-white/10 rounded-xl shadow-2xl overflow-hidden flex flex-col" data-id="element-4">
          {/* Screen top bar */}
          <div className="h-3 bg-white/5 flex items-center justify-center" data-id="element-5">
            <div className="w-6 h-1 rounded-full bg-white/10" data-id="element-6" />
          </div>
          {/* Screen content */}
          <div className="flex-1 flex flex-col items-center justify-center gap-3 p-4" data-id="element-7">
            <Icon className={`w-10 h-10 ${iconColor}`} data-id="element-8" />
            <div className="space-y-1.5 w-full" data-id="element-9">
              <div className="h-1.5 bg-white/10 rounded-full w-full" data-id="element-10" />
              <div className="h-1.5 bg-white/10 rounded-full w-4/5 mx-auto" data-id="element-11" />
              <div className="h-1.5 bg-white/10 rounded-full w-3/5 mx-auto" data-id="element-12" />
            </div>
            <div className="mt-2 space-y-1 w-full" data-id="element-13">
              <div className="h-1 bg-white/5 rounded-full w-full" data-id="element-14" />
              <div className="h-1 bg-white/5 rounded-full w-full" data-id="element-15" />
              <div className="h-1 bg-white/5 rounded-full w-2/3" data-id="element-16" />
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function PremiumBonusCard({
  bonus,
  index
}: {
  bonus: BonusData;
  index: number;
}) {
  const isReverse = index % 2 !== 0;
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.6,
    delay: index * 0.08
  }} className="relative overflow-hidden rounded-2xl border border-white/5" style={{
    background: 'linear-gradient(145deg, #1a2418 0%, #0f1a0f 50%, #1a2418 100%)'
  }} data-id="element-17">
      {/* FREE TODAY badge */}
      <div className="absolute top-4 right-4 z-10" data-id="element-18">
        <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-accent/20 border border-green-accent/30" data-id="element-19">
          <SparkleIcon className="w-3 h-3 text-green-accent" data-id="element-20" />
          <span className="font-sans text-[10px] sm:text-xs font-bold uppercase tracking-wider text-green-accent" data-id="element-21">
            Free Today
          </span>
        </div>
      </div>

      <div className={`flex flex-col ${isReverse ? 'md:flex-row-reverse' : 'md:flex-row'} items-center gap-6 sm:gap-8 p-6 sm:p-8 md:p-10`} data-id="element-22">
        {/* Mockup */}
        <div className="flex-shrink-0 w-full md:w-1/3 py-4" data-id="element-23">
          {bonus.image ? <div className="relative w-full flex items-center justify-center" data-id="element-24">
              <div className={`absolute inset-0 bg-gradient-to-br ${bonus.mockupColor} blur-2xl rounded-full opacity-60`} data-id="element-25" />
              <img src={bonus.image} alt={bonus.imageAlt || bonus.title} className="relative w-40 sm:w-48 h-auto rounded-xl shadow-2xl" data-id="element-26" />
            </div> : <MockupGraphic icon={bonus.icon} iconColor={bonus.iconColor} gradientClass={bonus.mockupColor} data-id="element-27" />}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-28">
          {/* Bonus label */}
          <div className="flex items-center gap-3 mb-3" data-id="element-29">
            <span className="inline-flex items-center px-3 py-0.5 rounded-full bg-gold/10 border border-gold/20" data-id="element-30">
              <span className="font-sans text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gold" data-id="element-31">
                Bonus #{bonus.number}
              </span>
            </span>
          </div>

          {/* Title */}
          <h3 className="font-serif text-2xl sm:text-3xl font-bold text-cream leading-snug mb-4" data-id="element-32">
            {bonus.title}
          </h3>

          {/* Description */}
          <p className="font-sans text-sm sm:text-base text-cream-muted leading-relaxed mb-5" data-id="element-33">
            {bonus.description}
          </p>

          {/* Value tag */}
          <div className="flex items-center gap-2" data-id="element-34">
            <BookIcon className="w-4 h-4 text-gold/60" data-id="element-35" />
            <span className="font-sans text-sm font-semibold text-gold" data-id="element-36">
              Valued at {bonus.value}
            </span>
            <span className="font-sans text-sm text-cream-muted line-through ml-1" data-id="element-37">
              {bonus.value}
            </span>
            <span className="font-sans text-sm font-bold text-green-accent ml-1" data-id="element-38">
              FREE
            </span>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function BonusPreviews() {
  return <section className="relative w-full py-20 sm:py-28 overflow-hidden" style={{
    background: 'linear-gradient(180deg, #111a11 0%, #0d160d 50%, #111a11 100%)'
  }} aria-label="Bonus previews" data-id="element-39">
      <div className="max-w-4xl mx-auto px-5 sm:px-8" data-id="element-40">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.7
      }} className="text-center mb-16" data-id="element-41">
          <p className="font-sans text-xs sm:text-sm tracking-[0.2em] uppercase text-gold/70 mb-3" data-id="element-42">
            Included Free
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-cream" data-id="element-43">
            5 Premium Bonuses to Accelerate Results
          </h2>
        </motion.div>

        {/* Premium Bonus Cards */}
        <div className="space-y-6 sm:space-y-8" data-id="element-44">
          {bonuses.map((bonus, index) => <PremiumBonusCard key={bonus.number} bonus={bonus} index={index} data-id="element-45" />)}
        </div>

        {/* Bottom divider */}
        <div className="mt-20 max-w-24 mx-auto h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" aria-hidden="true" data-id="element-46" />
      </div>
    </section>;
}